import type { DistrictsActivitiesBlockProps } from "./types";

import { useState } from "react";
import { Heading } from "@digdir/designsystemet-react";
import { ChevronDownIcon, ChevronUpIcon } from "@navikt/aksel-icons";

export type { DistrictsActivitiesBlockProps };

const colorMap = {
  Blue: {
    bg: "bg-tinted",
    btnText: "text-main",
    btnBorder: "border-main-subtle",
    btnBg: "bg-white hover:bg-main-surface-hover active:bg-main-surface-active",
    accHover: "group-hover:bg-brand-blue",
  },
  Neutral: {
    bg: "bg-tinted-neutral",
    btnText: "text-neutralNsf",
    btnBorder: "border-neutralNsf-subtle",
    btnBg:
      "bg-white hover:bg-neutralNsf-surface-hover active:bg-neutralNsf-surface-active",
    accHover: "group-hover:bg-brand-detail-gray-light",
  },
} as const;

const DistrictsActivitiesBlock = ({
  heading,
  colorTheme,
  items,
}: DistrictsActivitiesBlockProps) => {
  const theme = colorTheme ? colorMap[colorTheme] : colorMap.Neutral;

  const [open, setOpen] = useState(true);

  const handleToggle = (event: { currentTarget: { open: boolean } }) => {
    setOpen(event.currentTarget.open);
  };

  return (
    <div className={`${theme.bg} full-bleed py-4 lg:pt-10 lg:pb-16`}>
      <div className="content px-4">
        {/* Accordion/details on small screens  */}
        <details
          key={heading}
          open={open}
          onToggle={handleToggle}
          aria-expanded={open ? "true" : "false"}
          className="block lg:hidden"
        >
          <summary
            slot="summary"
            className="flex justify-between items-center py-4 group before:content-none focus-visible:outline-focusOuter hover:cursor-pointer md:py-6"
          >
            {heading && (
              <Heading className="heading-md mb-0">{heading}</Heading>
            )}
            {!open ? (
              <ChevronDownIcon
                title="Open"
                fontSize="1.5rem"
                className={`w-6 h-6 rounded-full text-neutralNsf-subtle ${theme.accHover} md:w-8 md:h-8`}
              />
            ) : (
              <ChevronUpIcon
                title="Close"
                fontSize="1.5rem"
                className={`w-6 h-6 rounded-full text-neutralNsf-subtle ${theme.accHover} md:w-8 md:h-8`}
              />
            )}
          </summary>
          <ul className="flex flex-col gap-4 pb-8">
            {items.map((link) => (
              <li key={link.title} className="!mb-0">
                <a
                  href={link.url}
                  className={`flex items-center w-full h-full p-4.5 heading-xs font-medium border outline-[3px] focus-visible:outline-focusOuter focus-visible:outline md:p-5.5
                  ${theme.btnBg} ${theme.btnText}  ${theme.btnBorder}
                `}
                >
                  {link.title}
                </a>
              </li>
            ))}
          </ul>
        </details>
        {/* Link block on larger screens  */}
        <div className="hidden lg:block">
          {heading && <Heading className="heading-md">{heading}</Heading>}
          <ul className="grid grid-cols-3 gap-8 justify-between">
            {items.map((link) => (
              <li key={link.title} className="!mb-0">
                <a
                  href={link.url}
                  className={`flex items-center w-full h-full p-4.5 heading-xs font-medium border outline-[3px] focus-visible:outline-focusOuter focus-visible:outline xl:p-6.5
                  ${theme.btnBg} ${theme.btnText}  ${theme.btnBorder}
                `}
                >
                  {link.title}
                </a>
              </li>
            ))}
          </ul>
        </div>
      </div>
    </div>
  );
};

export default DistrictsActivitiesBlock;
